import {
  MaybeRefOrGetter,
  Ref,
  computed,
  provide,
  readonly,
  ref,
  toValue,
  watchEffect,
} from "vue";
import { useRecaptchaPlugin } from "./plugin";
import {
  RecaptchaRenderOptions,
  executeRecaptcha,
  renderRecaptcha,
  resetRecaptcha,
} from "./utils";

export type RecaptchaAPI = {
  isReady: Readonly<Ref<boolean>>;
  sitekey: Readonly<Ref<string | null | undefined>>;
  widgetId: Readonly<Ref<string | null | undefined>>;
  token: Readonly<Ref<string | null | undefined>>;
  error: Ref<RecaptchaErrorType | null | undefined>;
  execute: () => Promise<string>;
  reset: () => void;
};

export type RecaptchaErrorType = "error" | "expired";

export const recaptchaContextKey = "recaptcha-context";

/**
 * Utilities that wrap Google ReCaptcha API.
 * https://developers.google.com/recaptcha/docs/invisible
 */
export function provideRecaptchaWidget(
  el: MaybeRefOrGetter<Element | null | undefined>,
  options?: Partial<RecaptchaRenderOptions>
): RecaptchaAPI {
  const { isReady, sitekey: defaultSitekey } = useRecaptchaPlugin();

  const widgetId = ref<string | null>();
  const token = ref<string | null>();
  const error = ref<RecaptchaErrorType | null>();

  const sitekey = computed(() => options?.sitekey ?? defaultSitekey);

  let resolveExecution: ((token: string) => void) | null;
  let rejectExecution: (() => void) | null;

  /**
   * Programmatically invoke the challenge.
   */
  const execute = (): Promise<string> => {
    guardIsReady();

    if (token.value) {
      return Promise.resolve(token.value);
    }

    return new Promise<string>((resolve, reject) => {
      resolveExecution = resolve;
      rejectExecution = reject;
      executeRecaptcha(widgetId.value);
    });
  };

  const reset = () => {
    guardIsReady();
    resetRecaptcha(widgetId.value);
    token.value = null;
    error.value = null;
  };

  // Install widget.
  watchEffect(() => {
    const elValue = toValue(el);

    if (!isReady.value || !elValue || !sitekey.value) {
      widgetId.value = null;
      return;
    }

    widgetId.value = renderRecaptcha(
      elValue,
      {
        sitekey: sitekey.value,
        size: options?.size ?? "invisible",
        theme: options?.theme ?? "light",
        tabindex: options?.tabindex,
      },
      (newToken) => {
        token.value = newToken;
        error.value = null;
        resolveExecution?.(newToken);
        resolveExecution = null;
        rejectExecution = null;
      },
      () => {
        token.value = null;
        error.value = "expired";
        rejectExecution?.();
        resolveExecution = null;
        rejectExecution = null;
      },
      () => {
        token.value = null;
        error.value = "error";
        rejectExecution?.();
        resolveExecution = null;
        rejectExecution = null;
      }
    );

    console.debug("Recaptcha widget rendered.", widgetId.value);
  });

  const context: RecaptchaAPI = {
    isReady: readonly(isReady),
    sitekey: readonly(sitekey),
    widgetId: readonly(widgetId),
    token: readonly(token),
    error,
    execute,
    reset,
  };

  provide(recaptchaContextKey, context);

  return context;

  function guardIsReady() {
    if (!isReady.value) {
      throw Error("Recaptcha error. API not loaded.");
    }
    if (!sitekey.value) {
      throw Error("Recaptcha error. Missing sitekey.");
    }
    if (typeof widgetId.value !== "number") {
      throw Error("Recaptcha error. Widget not loaded.");
    }
  }
}
